import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Calculator, TrendingDown, AlertTriangle, ArrowRight } from 'lucide-react';

export const FiscalSimulator = ({ onCtaClick }) => {
  const [budget, setBudget] = useState(50000);
  const [tauxFiscal, setTauxFiscal] = useState(25);
  const [showResult, setShowResult] = useState(false);

  // Calculs
  const economieActuelle = Math.round(budget * (tauxFiscal / 100));
  const coutReelActuel = budget - economieActuelle;
  const coutApresReforme = budget;
  const perte = economieActuelle;

  const handleCalculate = () => {
    setShowResult(true);
  };

  const formatCHF = (amount) => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount) + ' CHF';
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border-2 border-orange-200 overflow-hidden" data-testid="fiscal-simulator">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Calculator className="h-8 w-8" />
          <h3 className="text-2xl font-bold">Simulateur fiscal</h3>
        </div>
        <p className="text-white/90">
          Combien allez-vous perdre si vous attendez ?
        </p>
      </div>

      {/* Form */}
      <div className="p-6 space-y-6">
        {/* Budget */}
        <div>
          <Label className="text-base font-semibold mb-3 block">
            Budget travaux estimé
          </Label>
          <div className="flex items-center gap-4">
            <Input
              type="number"
              value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
              className="text-xl font-semibold text-right"
              min={5000}
              max={500000}
              step={5000}
            />
            <span className="text-lg font-medium text-muted-foreground">CHF</span>
          </div>
          <Slider
            value={[budget]}
            onValueChange={(v) => setBudget(v[0])}
            min={5000}
            max={200000}
            step={5000}
            className="mt-3"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>5'000 CHF</span>
            <span>200'000 CHF</span>
          </div>
        </div>

        {/* Taux fiscal */}
        <div>
          <Label className="text-base font-semibold mb-3 block">
            Taux d'imposition marginal estimé
          </Label>
          <div className="flex items-center gap-4">
            <Slider
              value={[tauxFiscal]}
              onValueChange={(v) => setTauxFiscal(v[0])}
              min={15}
              max={40}
              step={1}
              className="flex-1"
            />
            <span className="text-2xl font-bold text-orange-600 w-16 text-right">{tauxFiscal}%</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Taux moyen en Suisse romande : 20-30%
          </p>
        </div>

        {/* Bouton calcul */}
        {!showResult && (
          <Button 
            onClick={handleCalculate}
            className="w-full h-14 text-lg bg-orange-500 hover:bg-orange-600"
          >
            <Calculator className="mr-2 h-5 w-5" />
            Calculer mes économies
          </Button>
        )}

        {/* Résultats */}
        {showResult && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Aujourd'hui */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="font-semibold text-green-800">Aujourd'hui</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-green-700">Économie fiscale</p>
                  <p className="text-2xl font-bold text-green-600">{formatCHF(economieActuelle)}</p>
                </div>
                <div>
                  <p className="text-sm text-green-700">Coût réel</p>
                  <p className="text-2xl font-bold text-green-600">{formatCHF(coutReelActuel)}</p>
                </div>
              </div>
            </div>

            {/* Après réforme */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="font-semibold text-red-800">Après réforme (2028)</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-red-700">Économie fiscale</p>
                  <p className="text-2xl font-bold text-red-600">0 CHF</p>
                </div>
                <div>
                  <p className="text-sm text-red-700">Coût réel</p>
                  <p className="text-2xl font-bold text-red-600">{formatCHF(coutApresReforme)}</p>
                </div>
              </div>
            </div>

            {/* Perte */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <TrendingDown className="h-8 w-8" />
                  <div>
                    <p className="text-white/80 text-sm">Perte liée à l'attente</p>
                    <p className="text-3xl font-bold">{formatCHF(perte)}</p>
                  </div>
                </div>
                <AlertTriangle className="h-10 w-10 text-white/50" />
              </div>
            </div>

            {/* Message clé */}
            <div className="text-center py-4 border-t border-b">
              <p className="text-lg font-semibold text-gray-800">
                Attendre = payer <span className="text-red-600">plein tarif</span> pour le même projet
              </p>
            </div>

            {/* CTA */}
            <Button 
              onClick={onCtaClick}
              className="w-full h-14 text-lg bg-[var(--sage)] hover:bg-[var(--sage-dark)]"
            >
              Demander mon estimation gratuite
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
